import { graphql } from 'react-apollo';

import {
  getAdminInfo,
  assignmentWithAssessment,
  assignmentSessionTemplate,
  trackResultsVisit as trackResultsVisitQuery,
} from 'legacy-src/queries';
import {
  isPriorStudentsPage,
  parseNonEmptyJSONObject,
} from 'legacy-src/utility/utils';
import toast from 'chameleon/ui-stack/ui/toast';

// this is a handle to "debounce" the timer for refetching. Every time the page is ready, it'll set up a timeout to
// refetch if we are missing the required score data
let timeoutHandle = -1;
// canrefetch is so we only do one refetch,
let canRefetch;

export const withAssignment = graphql(assignmentWithAssessment, {
  options: (props) => {
    const {
      params,
      userData: { me },
      routes,
    } = props;
    const includePriorStudents = isPriorStudentsPage(routes);
    const hasScrambledQuestions =
      sessionStorage.getItem(`scrambled-order-${params.assignmentId}`) !==
      'false';
    const studentId = me.isStudent ? me.initId : null;
    return {
      variables: {
        assignmentId: params.assignmentId,
        studentId,
        includePriorStudents: includePriorStudents ? 'include' : null,
        masterSubjectId: params.subject,
        scrambledStudentId: hasScrambledQuestions ? studentId : null,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: (props) => {
    const {
      ownProps: {
        router: { routes },
        subjectData: {
          data: { classes },
        },
        userData: { isCoordinator, isAdmin },
        adminClasses,
      },
      data: { loading, assignment, quizIsPpc, refetch },
    } = props;

    // there is the case where a user can get to the results page before learnosity has finished processing the scores
    // if thats the case, we'll refetch. We are only refetching if all the values are empty, the page is in the ready state
    // and we're only going to refetch once.
    if (props.data.networkStatus == 1) {
      canRefetch = 1;
    }
    if (timeoutHandle != -1) {
      window.clearTimeout(timeoutHandle);
    }
    if (
      props.ownProps.router.location.pathname.indexOf('assessments/results/') >
        -1 &&
      assignment &&
      assignment.results == null &&
      assignment.studentScoringResults == null &&
      assignment.teacherScoringResults == null &&
      assignment.resultsByRubric == null &&
      assignment.resultsByItem == null &&
      // this is the ready state, for others, see https://github.com/apollographql/apollo-client/blame/main/src/core/networkStatus.ts
      props.data.networkStatus == 7
    ) {
      if (canRefetch) {
        timeoutHandle = window.setTimeout(() => {
          refetch();
          canRefetch = false;
        }, 60000);
      } else {
        toast(
          'There was a problem fetching score information, please try again',
          { autoClose: false },
        );
      }
    }

    const isAdminCoordinator = isAdmin || isCoordinator;

    if (loading) {
      return { assignmentResultsLoading: loading };
    }

    if (!assignment) {
      props.ownProps.router.replace(
        `/${props.ownProps.params.subject}/404-not-found`,
      );
      return null;
    }

    const includePriorStudents = routes.filter(
      (route) => route.name === 'priorStudents',
    ).length
      ? 'include'
      : null;

    const { students, studentsPrior } = assignment.assessment.isSampleQuiz
      ? {
          students: assignment.students,
          studentsPrior: [],
        }
      : (isAdminCoordinator ? adminClasses : classes || []).find(
          (section) => section.id === assignment.sectionId,
        ) || {};

    const assignment_students =
      includePriorStudents === 'include' ? studentsPrior : students;

    const {
      results,
      resultsByItem,
      assessment,
      questions,
      scoringRubricResults,
      responseAnalysis,
      scoringRubricResponseAnalysis,
      studentScoringRubricResults,
      teacherScoringRubricResults,
      feedback,
      studentResponses,
      scoringRubricResponses,
    } = assignment || {};

    const feedbackByStudent = (feedback || []).reduce((acc, cur) => {
      if (cur) {
        acc[cur.studentId] = cur.content;
      }
      return acc;
    }, {});

    return {
      studentResponses: parseNonEmptyJSONObject(studentResponses),
      scoringRubricResponses: parseNonEmptyJSONObject(scoringRubricResponses),
      learnosityRequestResults: results,
      learnosityRequestResultsByItem: resultsByItem,
      learnosityRequestResultsByRubric: scoringRubricResults,
      learnosityRequestResponseAnalysis: responseAnalysis,
      learnosityRequestScoringRubricResponseAnalysis:
        scoringRubricResponseAnalysis,
      learnosityRequestStudentScoringResults: studentScoringRubricResults,
      learnosityRequestTeacherScoringResults: teacherScoringRubricResults,
      questions,
      assignmentResultsLoading: false,
      assignment,
      assessment,
      feedback: feedbackByStudent,
      students: assignment_students,
      includePriorStudents,
      is_ppc: quizIsPpc || false,
    };
  },
});

export const withTrackResultsVisit = graphql(trackResultsVisitQuery, {
  props: ({ mutate, ownProps }) => ({
    trackResultsVisit: () =>
      mutate({
        variables: { assignmentId: ownProps.params.assignmentId },
      }),
  }),
});

export const withAdminInfo = graphql(getAdminInfo, {
  options: ({ params, educationPeriod }) => {
    const id = params.subject.toString();
    return { variables: { id, educationPeriod } };
  },
  props: ({ data }) =>
    !data.loading && data.adminSubject
      ? {
          adminClasses: data.adminSubject.subjectAllClasses,
        }
      : {
          adminClasses: [],
        },
});

export const withSessionTemplate = graphql(assignmentSessionTemplate, {
  options: ({ params }) => ({
    variables: {
      assignmentId: params.assignmentId,
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data }) => {
    if (data.assignment && !data.loading) {
      return {
        sessionTemplate: JSON.parse(data.assignment.sessionTemplate),
        loading: data.loading,
      };
    }

    return { isloading: data.loading };
  },
});
